/**
 * S3 API Service
 * Handles all S3-related API operations
 */
import { getProtected, postProtected, deleteProtected } from './ApiService';

/**
 * Upload attachment(s) to an asset
 * @param {number} assetId - Asset ID
 * @param {File|File[]} files - File or array of files to upload
 * @param {object} config - Additional axios config (for progress tracking, etc.)
 * @returns {Promise} Upload response
 */
export const uploadAttachment = async (assetId, files, config = {}) => {
  const formData = new FormData();
  
  if (Array.isArray(files)) {
    files.forEach(file => {
      formData.append('attachments', file);
    });
  } else {
    formData.append('attachments', files);
  }

  return await postProtected(
    `/api/assets/${assetId}/attachments/`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...config,
    }
  );
};

/**
 * Delete an attachment
 * @param {number} attachmentId - Attachment ID
 * @returns {Promise} Delete response
 */
export const deleteAttachment = async (attachmentId) => {
  return await deleteProtected(`/api/attachments/${attachmentId}/delete/`);
};

/**
 * Get presigned URL for downloading a private document
 * @param {number} attachmentId - Attachment ID
 * @returns {Promise<string>} Presigned URL
 */
export const getDownloadUrl = async (attachmentId) => {
  const response = await getProtected(`/api/attachments/${attachmentId}/download-url/`);
  return response.url;
};

/**
 * Check S3 health/connectivity
 * @returns {Promise} Health check response
 */
export const checkS3Health = async () => {
  return await getProtected('/api/s3/health-check/');
};

/**
 * Get asset with all attachments
 * @param {number} assetId - Asset ID
 * @returns {Promise} Asset data with attachments
 */
export const getAssetWithAttachments = async (assetId) => {
  return await getProtected(`/api/assets/${assetId}/`);
};

/**
 * Download a file from S3 using presigned URL
 * @param {number} attachmentId - Attachment ID
 * @param {string} filename - Desired filename for download
 */
export const downloadFile = async (attachmentId, filename = 'download') => {
  try {
    const url = await getDownloadUrl(attachmentId);
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Download failed:', error);
    throw error;
  }
};

/**
 * Get image URL (handles both public and private images)
 * @param {object} attachment - Attachment object
 * @returns {Promise<string>} Image URL
 */
export const getImageUrl = async (attachment) => {
  if (!attachment) return null;
  
  // If image_url is already provided (public image)
  if (attachment.image_url) {
    return attachment.image_url;
  }
  
  // If it's a private image, get presigned URL
  if (attachment.id && attachment.image) {
    return await getDownloadUrl(attachment.id);
  }
  
  return null;
};

export default {
  uploadAttachment,
  deleteAttachment,
  getDownloadUrl,
  checkS3Health,
  getAssetWithAttachments,
  downloadFile,
  getImageUrl,
};
